import { Link } from 'react-router-dom';
import { BadgeCheck, FlaskConical, FileText, Layers, Tag } from 'lucide-react';
import type { SourceType } from '@/content/financial/types';
import { SOURCE_LABELS } from '@/content/financial/types';

/**
 * The sourcing badge.
 *
 * This is the single most important piece of chrome in the Financial section.
 * A modelled figure and a figure pulled from a bank statement look identical on
 * a page; this badge is what forces the difference to be visible, and it links
 * to the methodology page that says what each tier cannot tell you.
 *
 * The verified tier is the only one styled in green. Everything else is
 * deliberately neutral — an "illustrative" badge that looks like a credential
 * would defeat the purpose.
 */
const STYLES: Record<SourceType, { icon: typeof BadgeCheck; className: string }> = {
  'operator-interview': {
    icon: BadgeCheck,
    className: 'text-green-700 dark:text-green-500',
  },
  'public-filing': { icon: FileText, className: 'text-ink-500 dark:text-ink-400' },
  'listing-data': { icon: Tag, className: 'text-ink-500 dark:text-ink-400' },
  composite: { icon: Layers, className: 'text-ink-500 dark:text-ink-400' },
  illustrative: { icon: FlaskConical, className: 'text-ink-500 dark:text-ink-400' },
};

export default function SourceBadge({
  sourceType,
  linked = false,
}: {
  sourceType: SourceType;
  /** Renders as a link to the methodology page. Off inside other links. */
  linked?: boolean;
}) {
  const { icon: Icon, className } = STYLES[sourceType];
  const label = SOURCE_LABELS[sourceType];

  const content = (
    <>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </>
  );

  const base = `inline-flex items-center gap-1 text-[11px] font-medium ${className}`;

  if (!linked) {
    return (
      <span className={base} title={`Sourcing: ${label}`}>
        {content}
      </span>
    );
  }

  return (
    <Link to="/financial/methodology" className={`${base} underline decoration-dotted underline-offset-2`}>
      {content}
    </Link>
  );
}
